import {
  CdkDrag,
  CdkDragDrop,
  CdkDragHandle,
  CdkDropList,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogConfig,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Subject, filter, finalize, first, forkJoin } from 'rxjs';
import { Api, ApiClient, ApiTypes } from '@app/api';
import { AccountService } from '@core/providers/account/account.service';
import { CategoriesBackendService } from '@core/providers/backend/categories-backend.service';
import { GoalsBackendService } from '@core/providers/backend/goals-backend.service';
import { NotificationService } from '@core/providers/notification/notification.service';
import { FoodbackLoaderComponent } from '@shared/components/foodback-loader/foodback-loader.component';
import { ValidationErrorsComponent } from '@shared/components/validation-errors/validation-errors.component';
import { CONSTANTS } from '@shared/constants';
import { ErrorsDirective } from '@shared/directives/error/errors.directive';
import { QuestionSets } from '@shared/enums/question-sets.enum';
import { FormModels } from '@shared/form-models';
import { UnsavedChangesService } from '@shared/guards';
import { CustomValidators } from '@shared/validators/custom-validators';
import {
  ConfigureCategoriesCategory,
  ConfigureCategoriesDialogInput,
} from './configure-categories-dialog.interface';

export const configureCategoriesDialogConfig: MatDialogConfig = {
  width: '600px',
  maxWidth: '95vw',
  disableClose: true,
};

const MAX_CATEGORY_NAME_LENGTH = 30;

@Component({
  selector: 'app-configure-categories-dialog',
  templateUrl: './configure-categories-dialog.component.html',
  styleUrls: ['./configure-categories-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatButton,
    MatFormField,
    MatLabel,
    MatInput,
    MatError,
    MatIcon,
    MatSlideToggle,
    CdkDropList,
    CdkDrag,
    CdkDragHandle,
    FoodbackLoaderComponent,
    ValidationErrorsComponent,
    ErrorsDirective,
    TranslatePipe,
  ],
})
export class ConfigureCategoriesDialogComponent implements OnInit {
  private dialogRef = inject<MatDialogRef<ConfigureCategoriesDialogComponent>>(MatDialogRef);
  private data = inject<ConfigureCategoriesDialogInput>(MAT_DIALOG_DATA);
  private formBuilder = inject(FormBuilder);
  private api = inject(ApiClient);
  private categoriesBackendService = inject(CategoriesBackendService);
  private goalsBackendService = inject(GoalsBackendService);
  private notificationService = inject(NotificationService);
  private accountService = inject(AccountService);
  private translateService = inject(TranslateService);
  private unsavedChangesService = inject(UnsavedChangesService);
  private destroyRef = inject(DestroyRef);

  readonly onClose$ = new Subject<ConfigureCategoriesCategory[]>();
  readonly isLoading = signal(true);
  readonly isSaving = signal(false);
  readonly namesEditable = signal(true);
  readonly errors = FormModels.VenueAccount.VenueErrors.categoryName;

  readonly configureForm = this.formBuilder.group({
    categories: this.formBuilder.array<FormGroup>([]),
    randomizeCategoryOrder: this.formBuilder.nonNullable.control(true),
  });

  private categoryUuids: string[] = [];

  constructor() {
    // `disableClose` keeps Material from closing on ESC or a backdrop click; both still
    // notify us, so entered input can be confirmed away instead of silently discarded.
    this.dialogRef
      .backdropClick()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.requestClose());
    this.dialogRef
      .keydownEvents()
      .pipe(
        filter(event => event.key === 'Escape'),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.requestClose());
  }

  get categoriesArray(): FormArray<FormGroup> {
    return this.configureForm.controls.categories;
  }

  ngOnInit(): void {
    forkJoin({
      categories: this.categoriesBackendService.getAllCategoriesForLoggedAccount(
        this.data.accountUuid,
        CONSTANTS.CONTEXT.ACCOUNT
      ),
      settings: this.api.get(Api.surveys.surveyCategoryAccount.accounts.settings, {
        pathParams: { accountUuid: this.data.accountUuid },
      }),
      account: this.accountService.getAccountContext$().pipe(first()),
    })
      .pipe(
        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({ categories, settings, account }) => {
        this.namesEditable.set(account.questionSet === QuestionSets.FLEXIBLE);
        this.buildForm(categories, settings.randomizeCategoryOrder);
      });
  }

  drop(event: CdkDragDrop<FormGroup[]>): void {
    this.moveCategory(event.previousIndex, event.currentIndex);
  }

  cancel(): void {
    this.dialogRef.close();
  }

  /** Closing via ESC or the backdrop must not silently drop what the user changed. */
  requestClose(): void {
    if (!this.configureForm.dirty) {
      this.dialogRef.close();
      return;
    }

    this.unsavedChangesService
      .confirmDiscard$()
      .pipe(first(), filter(Boolean), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.dialogRef.close());
  }

  submit(): void {
    this.configureForm.markAllAsTouched();
    if (this.configureForm.invalid || this.isSaving()) {
      return;
    }

    const orderedCategories: ConfigureCategoriesCategory[] = this.categoriesArray.controls.map(
      (group, index) => ({
        uuid: this.categoryUuids[index],
        name: String(group.controls.categoryName.value ?? '').trim(),
      })
    );

    const body: Api.surveys.surveyCategoryAccount.accounts.PutBody = {
      categories: orderedCategories.map((category, index) => ({
        ...category,
        position: index + 1,
      })),
      randomizeCategoryOrder: this.configureForm.controls.randomizeCategoryOrder.value,
    };

    this.isSaving.set(true);
    this.api
      .put(Api.surveys.surveyCategoryAccount.accounts, {
        pathParams: { accountUuid: this.data.accountUuid },
        body,
      })
      .pipe(
        finalize(() => this.isSaving.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.categoriesBackendService.clearCachedData();
        this.goalsBackendService.resetAllCachedData();
        this.onClose$.next(orderedCategories);
        this.notificationService.confirmWithKey(
          'SHARED.QUESTIONS.CONFIGURE_CATEGORIES_DIALOG.SAVE_SUCCESS'
        );
        this.dialogRef.close();
      });
  }

  private buildForm(
    categories: ApiTypes.SurveyCategoryListItemResponseDto[],
    randomizeCategoryOrder: boolean
  ): void {
    const configurableCategories = categories.filter(category => !category.overallExperience);
    this.categoryUuids = configurableCategories.map(category => category.uuid);
    const nameValidators = this.namesEditable()
      ? [
          Validators.required,
          Validators.minLength(1),
          Validators.maxLength(MAX_CATEGORY_NAME_LENGTH),
          CustomValidators.noWhitespaceValidator,
        ]
      : [];

    configurableCategories.forEach(category => {
      this.categoriesArray.push(
        this.formBuilder.group({
          categoryName: this.formBuilder.nonNullable.control(
            this.resolveName(category),
            nameValidators
          ),
        })
      );
    });

    this.configureForm.controls.randomizeCategoryOrder.setValue(randomizeCategoryOrder);
  }

  private resolveName(category: ApiTypes.SurveyCategoryListItemResponseDto): string {
    return (
      category.flexibleName ??
      this.translateService.instant(`SHARED.BACKEND_LABELS.${category.name.toUpperCase()}`)
    );
  }

  private moveCategory(from: number, to: number): void {
    if (to < 0 || to >= this.categoriesArray.length || from === to) {
      return;
    }

    const control = this.categoriesArray.at(from);
    this.categoriesArray.removeAt(from);
    this.categoriesArray.insert(to, control);
    moveItemInArray(this.categoryUuids, from, to);
    this.categoriesArray.markAsDirty();
  }
}
